import React, {FC, memo, useState} from "react";
import {Alert, Radio, Select, Skeleton} from "antd";
import {cyan, lime, orange, purple} from "@ant-design/colors";
import Page from "../../../views/Page/Page";
import Section from "../../../views/Section/Section";
import {ReportCompare, ReportPeriod, useReportDashboardQuery, useReportLocationsQuery} from "../../../generated/graphql";
import css from "./Reports.less";
import {compareLabels, periodLabels, shortDay} from "./labels";
import {CompareTile, Dashboard, HeatmapTile, PotentialTile, SourcesTile} from "./tiles";
import {HelpIcon, TileId, docsUrl} from "./help";

/**
 * Dashboard-Tab „Auswertungen“ (Zusatzmodul, Flag featureReports). Vier Kacheln aus den nächtlichen
 * Fakten-Tabellen; das Potenzial rechnet live. Reihenfolge fest, Layout je Nutzer folgt mit mehr Kacheln.
 */

type TileDef = {
    id: TileId;
    title: string;
    subtitle: (period: ReportPeriod, compare: ReportCompare, location: string) => string;
    // Farbton des Kachelkopfs (Variante A); die Daten bleiben blau.
    tint: string;
    Body: FC<{data: Dashboard; period: ReportPeriod; compare: ReportCompare}>;
};

const tiles: Array<TileDef> = [
    {
        id: "potential",
        title: "Ungenutztes Potenzial",
        subtitle: (_p, _c, loc) => `Freie Plätze mal Kurspreis, ${loc}, laufende Kurse und Starts in 30 Tagen`,
        tint: orange[1],
        Body: PotentialTile,
    },
    {
        id: "heatmap",
        title: "Auslastung nach Wochentag und Uhrzeit",
        subtitle: (_p, _c, loc) => `Belegte Plätze je Einheit über den ganzen Zeitraum, ${loc}`,
        tint: lime[1],
        Body: HeatmapTile,
    },
    {
        id: "compare",
        title: "Geschäft im Vergleich",
        subtitle: (p, c) => `${periodLabels[p]} gegen ${compareLabels[c]}. Verlauf: letzte zwölf Monate`,
        tint: purple[1],
        Body: CompareTile,
    },
    {
        id: "sources",
        title: "Woher die Buchungen kommen",
        subtitle: (p, _c, loc) => `${periodLabels[p]}, ${loc}`,
        tint: cyan[1],
        Body: SourcesTile,
    },
];

const ALL_LOCATIONS = "Alle Standorte";

const Reports: FC = () => {
    const [period, setPeriod] = useState<ReportPeriod>(ReportPeriod.Month);
    const [compare, setCompare] = useState<ReportCompare>(ReportCompare.Previous);
    const [locationId, setLocationId] = useState<string | undefined>(undefined);
    const {data: locationData} = useReportLocationsQuery({fetchPolicy: "cache-first"});
    const {data, loading, error} = useReportDashboardQuery({variables: {period, compare, locationId}, fetchPolicy: "cache-and-network"});

    const locations = locationData?.locations ?? [];
    const locationName = locations.find((l) => l.id === locationId)?.name ?? ALL_LOCATIONS;
    const dashboard = data?.reportDashboard;
    const disabled = error?.graphQLErrors.some((e) => e.extensions?.code === "REPORTS_DISABLED") ?? false;

    const toolbar = (
        <div className={css.toolbar}>
            <Radio.Group value={period} onChange={(e) => setPeriod(e.target.value)} size="small">
                <Radio.Button value={ReportPeriod.Week}>{"Woche"}</Radio.Button>
                <Radio.Button value={ReportPeriod.Month}>{"Monat"}</Radio.Button>
                <Radio.Button value={ReportPeriod.Quarter}>{"Quartal"}</Radio.Button>
                <Radio.Button value={ReportPeriod.Year}>{"Jahr"}</Radio.Button>
            </Radio.Group>
            <Select<ReportCompare> size="small" value={compare} onChange={setCompare} style={{width: 170}}>
                <Select.Option value={ReportCompare.Previous}>{"gegen Vorperiode"}</Select.Option>
                <Select.Option value={ReportCompare.LastYear}>{"gegen Vorjahr"}</Select.Option>
            </Select>
            {locations.length > 1 ? (
                <Select<string> size="small" value={locationId ?? ""} onChange={(value) => setLocationId(value || undefined)} style={{width: 220}}>
                    <Select.Option value="">{ALL_LOCATIONS}</Select.Option>
                    {locations.map((l) => (
                        <Select.Option key={l.id} value={l.id}>
                            {l.name}
                        </Select.Option>
                    ))}
                </Select>
            ) : null}
        </div>
    );

    return (
        <Page
            content={
                <Section
                    title="Auswertungen"
                    menu={toolbar}
                    minPadding
                    content={
                        <div className={css.root}>
                            {error && disabled ? (
                                <Alert
                                    type="warning"
                                    showIcon
                                    style={{margin: 16}}
                                    message="Auswertungen sind für diese Schule nicht freigeschaltet"
                                    description="Das Modul wird je Schwimmschule aktiviert. Melde dich beim Support, wenn du es nutzen möchtest."
                                />
                            ) : null}
                            {error && !disabled ? (
                                <Alert
                                    type="error"
                                    showIcon
                                    style={{margin: 16}}
                                    message="Die Auswertungen konnten gerade nicht geladen werden"
                                    description="Bitte in ein paar Minuten noch einmal versuchen."
                                />
                            ) : null}
                            {dashboard || (loading && !error) ? (
                                <div className={css.grid}>
                                    {tiles.map((tile) => (
                                        <div key={tile.id} className={css.tile} style={{"--tint": tile.tint} as React.CSSProperties}>
                                            <div className={css.tileHead}>
                                                <div>
                                                    <h4 className={css.tileTitle}>{tile.title}</h4>
                                                    {dashboard ? <div className={css.tileSubtitle}>{tile.subtitle(period, compare, locationName)}</div> : null}
                                                </div>
                                                <HelpIcon id={tile.id} title={tile.title} />
                                            </div>
                                            <div className={css.tileBody}>
                                                {dashboard ? <tile.Body data={dashboard} period={period} compare={compare} /> : <Skeleton active paragraph={{rows: 5}} />}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            {dashboard ? (
                                <div className={css.footnote}>
                                    {dashboard.dataThrough
                                        ? `Zahlen bis ${shortDay(dashboard.dataThrough)}, jede Nacht aktualisiert. Potenzial: Stand jetzt. `
                                        : "Die Zahlen werden nächtlich aufbereitet; der erste Lauf steht noch aus. Potenzial: Stand jetzt. "}
                                    {"Was hinter jeder Zahl steckt, erklärt das Info-Symbol in der Kachel und die "}
                                    <a href={docsUrl()} target="_blank" rel="noopener noreferrer">
                                        {"Anleitung"}
                                    </a>
                                    {"."}
                                </div>
                            ) : null}
                        </div>
                    }
                />
            }
        />
    );
};

export default memo(Reports);
